import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AcsCard, AcsLogo, AcsPageShell } from '../components';
import { useI18n } from '../i18n/I18nProvider';
import { api } from '../api/client';
import { KioskForm } from '../kiosk/KioskForm';

/**
 * Anteprima del kiosk per il personale.
 *
 * Rende lo stesso componente che vede il visitatore, con la stessa informativa
 * e lo stesso testo di consenso, ma **senza alcun percorso di invio**: la
 * `onSubmit` non esiste e il server non apre nessuna sessione kiosk per questa
 * pagina. Un'anteprima non può quindi creare un contatto né un evento di
 * consenso, il che è esattamente la garanzia che serve (§10.3).
 */
export function KioskPreviewPage() {
  const { id = '' } = useParams();
  const { t, language } = useI18n();

  const preview = useQuery({
    queryKey: ['kiosk-preview', id, language],
    queryFn: () => api.kioskPreview(id, language),
    retry: false,
  });

  return (
    <AcsPageShell header={<AcsLogo />}>
      <Link to={`/app/events/${id}`} style={{ color: 'var(--acs-color-primary)' }}>
        ← {t.app.back}
      </Link>

      <h1>{t.preview.title}</h1>

      {preview.isLoading ? <p>{t.common.loading}</p> : null}

      {preview.isError ? (
        <AcsCard>
          <p>{t.preview.noPolicy}</p>
        </AcsCard>
      ) : null}

      {preview.data ? (
        <>
          <div
            role="note"
            style={{
              background: 'var(--acs-color-highlight)',
              color: 'var(--acs-color-highlight-contrast)',
              padding: 'var(--acs-space-4)',
              borderRadius: 'var(--acs-radius-md)',
              marginBottom: 'var(--acs-space-5)',
            }}
          >
            {t.preview.banner}
          </div>

          {/*
            `preview` disabilita l'invio nel componente stesso: non si passa un
            handler inerte, così non esiste nemmeno un ramo di codice che
            potrebbe inviare per errore.
          */}
          <KioskForm config={preview.data} preview submitting={false} onSubmit={() => undefined} />
        </>
      ) : null}
    </AcsPageShell>
  );
}
